import dayjs from 'dayjs';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ThemeProvider } from '@mui/material/styles';
import {
  Card,
  Container,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableContainer,
  TableFooter,
  TablePagination,
  TableCell,
  Stack,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';

import {
  CustomIconButton,
  DefaultContainedButton,
  GreenOutlinedButton,
  RedOutlinedButton,
} from '../../../utils/Buttons';
import { CustomTextField } from '../../../utils/StyledInputs';
import { DecisionDialog } from '../../../utils/DecisionDialog';
import { TablePaginationActions, theme } from '../../../utils/TableStyles';
import API from '../../../api';
import { ErrorUI, HttpErrorHandler } from '../../../utils/ErrorHandler';
import { usePopup } from '../../../context/PopupContext';

export default function Filters() {
  const navigate = useNavigate();
  const { showPopup, setPopUpText } = usePopup();

  // Data
  const [filters, setFilters] = useState([]);
  const [totalFilters, setTotalFilters] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasAuth, setHasAuth] = useState(false);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimerRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(0);

  // Fetch filters (server-side pagination)
  const fetchFilters = (currentPage, search) => {
    setLoading(true);
    API.get('/filters/paginated', { params: { page: currentPage + 1, ...(search && { search }) } })
      .then((response) => {
        setFilters(response.data.data || []);
        setTotalFilters(response.data.total || 0);
      })
      .catch((e) => {
        HttpErrorHandler(e, setErrorMessage);

        setFilters([]);
        setTotalFilters(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const data = JSON.parse(localStorage.getItem('userData'));
    if (data?.can_manage_filters) {
      setHasAuth(true);
    }
    fetchFilters(page, searchQuery);
  }, [page]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  // Handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    debounceTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchFilters(0, value);
    }, 500);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);

  // Navigation
  const handleCreate = () => {
    navigate('/admin/filters/filter-details', { state: { mode: 'create' } });
  };

  const handleEdit = (filter) => {
    navigate('/admin/filters/filter-details', { state: { mode: 'edit', filterID: filter.id } });
  };

  // Status toggle
  const [statusDialog, setStatusDialog] = useState({ open: false, filter: null });

  const handleToggleStatus = () => {
    const { filter } = statusDialog;
    const newStatus = filter.status === 'draft' ? 'visible' : 'draft';

    API.patch(`/filters/${filter.id}/status`, { status: newStatus })
      .then((response) => {
        fetchFilters(page, searchQuery);
        showPopup(
          true,
          response.data.message || `Filtro ${newStatus === 'visible' ? 'pubblicato' : 'riportato in bozza'}`
        );
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setStatusDialog({ open: false, filter: null }));
  };

  // Archive toggle
  const [archiveDialog, setArchiveDialog] = useState({ open: false, filter: null });

  const handleToggleArchive = () => {
    const { filter } = archiveDialog;

    API.patch(`/filters/${filter.id}/archive`, { is_archived: !filter.is_archived })
      .then((response) => {
        fetchFilters(page, searchQuery);
        showPopup(true, response.data.message || `Filtro ${filter.is_archived ? 'disarchiviato' : 'archiviato'}`);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      })
      .finally(() => setArchiveDialog({ open: false, filter: null }));
  };

  return (
    <>
      <Helmet>
        <title>Filtri</title>
      </Helmet>

      <Container maxWidth="xl">
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Filtri</Typography>
          <DefaultContainedButton
            onClick={handleCreate}
            startIcon={<AddIcon />}
            customStyles={{ display: hasAuth ? 'inline-flex' : 'none' }}
          >
            Nuovo Filtro
          </DefaultContainedButton>
        </Stack>

        {errorMessage?.length > 0 ? (
          ErrorUI(errorMessage)
        ) : (
          <Card>
            {/* Search bar */}
            <Stack sx={{ px: 2, pt: 2 }}>
              <CustomTextField
                size="small"
                placeholder="Cerca filtro per nome o per entità"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#637381' }} />
                    </InputAdornment>
                  ),
                }}
                customStyles={{ maxWidth: 400 }}
              />
            </Stack>

            <TableContainer sx={{ padding: '24px' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell align="center">Entità</TableCell>
                    <TableCell align="center">Condizioni Applicate</TableCell>
                    <TableCell align="center">Utenti Risultanti</TableCell>
                    <TableCell align="center">Stato</TableCell>
                    <TableCell align="center">Archiviato</TableCell>
                    <TableCell align="center">Ultimo Aggiornamento</TableCell>
                    <TableCell align="right">Azioni</TableCell>
                  </TableRow>
                </TableHead>
                {loading ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ p: 2 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : filters.length === 0 ? (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Nessun filtro trovato
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  <TableBody>
                    {filters.map((filter) => (
                      <TableRow
                        key={filter.id}
                        hover
                        sx={{ cursor: 'pointer', height: '53px' }}
                        onClick={() => handleEdit(filter)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="span">{filter.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">{filter.entity.name}</TableCell>
                        <TableCell align="center">
                          <Chip label={filter.conditions.length} size="small" color="primary" sx={{ minWidth: 32 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={filter.users_count} size="small" color="info" sx={{ minWidth: 32 }} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={
                              filter.status === 'visible' ? 'Clicca per riportare in bozza' : 'Clicca per pubblicare'
                            }
                            arrow
                          >
                            <Chip
                              label={filter.status === 'visible' ? 'Pubblico' : 'Bozza'}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                backgroundColor: filter.status === 'visible' ? '#e8f5e9' : '#ffebee',
                                color: filter.status === 'visible' ? '#2e7d32' : '#c62828',
                                '&:hover': {
                                  backgroundColor: filter.status === 'visible' ? '#c8e6c9' : '#ffcdd2',
                                  color: filter.status === 'visible' ? '#1b5e20' : '#b71c1c',
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusDialog({ open: true, filter });
                              }}
                            />
                          </Tooltip>
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip
                            title={filter.is_archived ? 'Clicca per disarchiviare' : 'Clicca per archiviare'}
                            arrow
                          >
                            <Chip
                              label={filter.is_archived ? 'Archiviato' : 'Attivo'}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                backgroundColor: filter.is_archived ? '#fff3e0' : '#e3f2fd',
                                color: filter.is_archived ? '#e65100' : '#1565c0',
                                '&:hover': {
                                  backgroundColor: filter.is_archived ? '#ffe0b2' : '#bbdefb',
                                  color: filter.is_archived ? '#bf360c' : '#0d47a1',
                                },
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setArchiveDialog({ open: true, filter });
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          {dayjs(filter.updated_at).locale('it').format('DD/MM/YYYY')}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" justifyContent="flex-end" spacing={0}>
                            <Tooltip title="Modifica">
                              <CustomIconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEdit(filter);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </CustomIconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
                <TableFooter>
                  <TableRow>
                    <ThemeProvider theme={theme}>
                      <TablePagination
                        sx={{ borderBottom: 'none' }}
                        rowsPerPageOptions={[]}
                        colSpan={8}
                        count={totalFilters}
                        rowsPerPage={10}
                        page={page}
                        onPageChange={handleChangePage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </ThemeProvider>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Container>

      <DecisionDialog
        openDialog={statusDialog.open}
        dialogTitle={statusDialog.filter?.status === 'draft' ? 'Pubblica filtro' : 'Riporta in bozza'}
        dialogDescription={
          statusDialog.filter?.status === 'draft'
            ? `Sei sicuro di voler pubblicare il filtro "${statusDialog.filter?.name}"?`
            : `Sei sicuro di voler riportare in bozza il filtro "${statusDialog.filter?.name}"?`
        }
        closeOperation={() => setStatusDialog({ open: false, filter: null })}
        requestOperation={handleToggleStatus}
        requestText={statusDialog.filter?.status === 'draft' ? 'Pubblica' : 'Riporta in bozza'}
        RequestButton={statusDialog.filter?.status === 'draft' ? GreenOutlinedButton : RedOutlinedButton}
        closeText="Annulla"
      />

      <DecisionDialog
        openDialog={archiveDialog.open}
        dialogTitle={archiveDialog.filter?.is_archived ? 'Disarchivia filtro' : 'Archivia filtro'}
        dialogDescription={
          archiveDialog.filter?.is_archived
            ? `Sei sicuro di voler disarchiviare il filtro "${archiveDialog.filter?.name}"?`
            : `Sei sicuro di voler archiviare il filtro "${archiveDialog.filter?.name}"?`
        }
        closeOperation={() => setArchiveDialog({ open: false, filter: null })}
        requestOperation={handleToggleArchive}
        requestText={archiveDialog.filter?.is_archived ? 'Disarchivia' : 'Archivia'}
        RequestButton={archiveDialog.filter?.is_archived ? GreenOutlinedButton : RedOutlinedButton}
        closeText="Annulla"
      />
    </>
  );
}
